/**
 * App build identity + deployed-version probe (task #200).
 *
 * Why this exists: the SPA shell (index.html) is served behind a CDN with a
 * long `s-maxage`, so a returning browser can keep running an OLD hashed-chunk
 * bundle after a deploy until a hard refresh. That stranded users on stale code
 * (the recurring cause behind the "zone overlay still broken" / "zones failed
 * to load" reports - both were fixed in shipped builds the user had not yet
 * picked up). Rather than depend on cache headers being perfect, the running
 * app ACTIVELY notices a newer build and offers a reload.
 *
 * Mechanism:
 *   - `APP_BUILD_ID` is baked into THIS bundle at build time (vite `define`).
 *   - The deployed build id lives in `/version.json` (emitted by the vite
 *     plugin), fetched with cache-busting + `no-store` so neither the browser
 *     nor the CDN can hand back a stale value.
 *   - When the deployed id differs from the baked id, a newer bundle has
 *     shipped and this tab is stale -> AppUpdateBanner prompts a reload.
 *
 * Pure + injectable: `fetchDeployedBuildId` takes `now` for the cache-bust
 * query and `isUpdateAvailable` takes the baked id, so both are unit-testable
 * without globals or a real clock.
 */

/**
 * Build identity of THIS running bundle, baked in by vite `define` at build
 * time. Falls back to 'dev' when the define is absent (e.g. a unit-test or
 * non-vite context) so the update check never nags outside a real deploy.
 */
export const APP_BUILD_ID: string =
  typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'dev';

/** Shape of the emitted /version.json. Only `buildId` is required by callers. */
export interface VersionManifest {
  /** `<version>+<ISO build timestamp>`; compared to APP_BUILD_ID. */
  buildId: string;
  /** package.json version, for display/debug. Optional. */
  version?: string;
  /** ISO build timestamp, for display/debug. Optional. */
  builtAt?: string;
}

/**
 * Fetch the deployed build id from `/version.json`.
 *
 * Cache-busted (`?t=<now>`) AND `cache: 'no-store'` so it bypasses BOTH the
 * browser cache and the CDN edge cache (the unique query string defeats the
 * CDN's `s-maxage`). Returns `null` on ANY failure (offline, non-2xx, parse
 * error, missing/empty field) so a transient blip reads as "no update known"
 * rather than a false prompt.
 *
 * @param now Wall-clock ms for the cache-bust query (caller passes Date.now()).
 * @returns The deployed buildId, or null when it cannot be determined.
 */
export async function fetchDeployedBuildId(now: number): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${now}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<VersionManifest>;
    return typeof data.buildId === 'string' && data.buildId.length > 0
      ? data.buildId
      : null;
  } catch {
    return null;
  }
}

/**
 * True when the deployed build id indicates a NEWER bundle than this tab booted
 * with.
 *
 * Guards:
 *   - A null/empty deployed id (probe failed) is NOT an update (no false nag).
 *   - A baked id of 'dev' (no real build) never reports an update, so local dev
 *     and unit tests stay quiet.
 *
 * @param deployedBuildId The id read from /version.json (or null).
 * @param bakedBuildId The id baked into this bundle (defaults to APP_BUILD_ID).
 * @returns true when a reload would pick up newer code.
 */
export function isUpdateAvailable(
  deployedBuildId: string | null,
  bakedBuildId: string = APP_BUILD_ID,
): boolean {
  if (!deployedBuildId) return false;
  if (bakedBuildId === 'dev') return false;
  return deployedBuildId !== bakedBuildId;
}
